import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  IconButton,
  Text,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react'
import { IconPencil, IconSend, IconShieldCheck, IconWorld, IconX } from '@tabler/icons-react'
import React, { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InternetSearchApprovalState, InternetSearchDecision, InternetSearchFinding } from '../types/chatTypes'

// Longest query the resume endpoint accepts (`MAX_APPROVED_QUERY_CHARS`).
const MAX_QUERY_CHARS = 500

// The marks layer and the textarea share these metrics, so the marks sit under the right words.
const FIELD = {
  pl: '12px',
  pr: '52px',
  py: '8px',
  fontSize: '14px',
  lineHeight: '22px',
  borderWidth: '1px',
  borderRadius: '12px',
}

const contains = (query: string, finding: InternetSearchFinding) =>
  query.toLowerCase().includes(finding.text.toLowerCase())

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Splits the query into plain and marked parts; marks follow the literal text only.
const markParts = (query: string, findings: InternetSearchFinding[]) => {
  const live = findings.filter(finding => contains(query, finding))
  if (!live.length) return [{ text: query, marked: false }]
  const texts = live.map(finding => finding.text).sort((a, b) => b.length - a.length)
  const lowered = texts.map(text => text.toLowerCase())
  return query.split(new RegExp(`(${texts.map(escapeRegExp).join('|')})`, 'gi')).map(text => ({
    text,
    marked: lowered.includes(text.toLowerCase()),
  }))
}

export interface InternetSearchApprovalProps {
  approval: InternetSearchApprovalState
  // `queries` is aligned with `approval.searches`: the edited query, or null when dropped.
  onResolve?: (decision: InternetSearchDecision, queries?: (string | null)[]) => void
}

/**
 * Shown when the outgoing-query check found something: each flagged query,
 * editable with its findings marked, to send, drop, or skip altogether.
 */
export const InternetSearchApproval: React.FC<InternetSearchApprovalProps> = ({ approval, onResolve }) => {
  const { t } = useTranslation()
  const [edits, setEdits] = useState(() => approval.searches.map(search => search.query))
  const [dropped, setDropped] = useState<number[]>([])
  const id = useId()
  const background = useColorModeValue('white', 'gray.800')
  const muted = useColorModeValue('gray.600', 'gray.400')
  const border = useColorModeValue('gray.200', 'gray.600')
  const fieldBackground = useColorModeValue('gray.50', 'gray.900')
  const markBackground = useColorModeValue('orange.100', 'orange.800')
  const removedColor = useColorModeValue('green.600', 'green.300')
  const editBorder = useColorModeValue('gray.400', 'gray.500')

  const translate = (key: string) => t(`internetSearch.approval.${key}`)

  if (approval.status === 'internal') {
    return (
      <Flex role='status' align='center' gap={2} color={muted} fontSize='sm' py={3}>
        <Icon as={IconShieldCheck} boxSize={4} flexShrink={0} aria-hidden />
        <Text>{translate(approval.has_internal_tools ? 'internalStatus' : 'noInternetStatus')}</Text>
      </Flex>
    )
  }

  // After "send" the answer streams in its place; nothing is left to show.
  if (approval.status === 'sent') return null

  // What "send" would answer, aligned with `searches`: an empty field counts as dropped.
  const answers = edits.map((edit, i) => (dropped.includes(i) || !edit.trim() ? null : edit.trim()))
  const tooLong = (i: number) => !dropped.includes(i) && edits[i].length > MAX_QUERY_CHARS
  const nothingLeft = !answers.some(Boolean) && !approval.other_queries.length
  const canSend = Boolean(onResolve) && !nothingLeft && !edits.some((_, i) => tooLong(i))

  // The most serious case on the card decides: marks, then a failed check, then clean (always ask).
  const marked = approval.searches.some(search => !search.check_failed && search.findings.length)
  const unchecked = approval.searches.some(search => search.check_failed)
  const kind = marked ? '' : unchecked ? 'Unchecked' : 'Clean'
  const description = `description${kind}${approval.searches.length > 1 ? 'Many' : 'One'}`
  const setEdit = (i: number, value: string) => setEdits(prev => prev.map((edit, k) => (k === i ? value : edit)))

  return (
    <Box
      as='form'
      aria-labelledby={`${id}-title`}
      bg={background}
      borderWidth='1px'
      borderColor={border}
      borderRadius='2xl'
      p={{ base: 4, md: 5 }}
      my={4}
      maxW='640px'
      width='100%'
      data-dd-privacy='mask'
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault()
        if (canSend) onResolve?.('send', answers)
      }}
    >
      <Flex align='center' gap={2.5} mb={2}>
        <Icon as={IconWorld} boxSize={5} flexShrink={0} aria-hidden />
        <Text as='h3' id={`${id}-title`} fontWeight='semibold' fontSize='md'>
          {translate('title')}
        </Text>
      </Flex>
      <Text fontSize='sm' color={muted} mb={4}>
        {translate(description)}
      </Text>

      {approval.searches.map((search, i) => {
        const findings = search.check_failed ? [] : search.findings
        const findingsId = `${id}-findings-${i}`
        return (
          <Box key={search.index} mb={3}>
            {dropped.includes(i) ? (
              <>
                <Flex
                  align='center'
                  justify='space-between'
                  gap={2}
                  borderWidth='1px'
                  borderStyle='dashed'
                  borderColor={border}
                  borderRadius={FIELD.borderRadius}
                  px='12px'
                  py='8px'
                >
                  <Text as='s' fontSize='14px' color={muted} overflowWrap='anywhere'>
                    {edits[i]}
                  </Text>
                  <Button
                    size='xs'
                    variant='link'
                    flexShrink={0}
                    onClick={() => setDropped(prev => prev.filter(k => k !== i))}
                  >
                    {translate('undrop')}
                  </Button>
                </Flex>
                <Text fontSize='xs' color={muted} mt={1}>
                  {translate('dropped')}
                </Text>
              </>
            ) : (
              <FormControl isInvalid={tooLong(i)}>
                <Box position='relative' bg={fieldBackground} borderRadius={FIELD.borderRadius}>
                  {/* In flow, so the field grows with the wrapped text; the textarea lies on top. */}
                  <Box
                    aria-hidden
                    {...FIELD}
                    borderColor='transparent'
                    whiteSpace='pre-wrap'
                    overflowWrap='anywhere'
                    color='transparent'
                    pointerEvents='none'
                  >
                    {markParts(edits[i], findings).map((part, k) =>
                      part.marked ? (
                        <Box
                          as='mark'
                          key={k}
                          bg={markBackground}
                          color='transparent'
                          borderRadius='4px'
                        >
                          {part.text}
                        </Box>
                      ) : (
                        <React.Fragment key={k}>{part.text}</React.Fragment>
                      ),
                    )}{' '}
                  </Box>
                  <Textarea
                    {...FIELD}
                    aria-label={translate('queryLabel')}
                    aria-describedby={findings.length ? findingsId : undefined}
                    value={edits[i]}
                    onChange={event => setEdit(i, event.target.value)}
                    maxLength={MAX_QUERY_CHARS}
                    position='absolute'
                    inset={0}
                    h='100%'
                    minH={0}
                    rows={1}
                    resize='none'
                    overflow='hidden'
                    bg='transparent'
                    borderColor={border}
                    _hover={{ borderColor: editBorder }}
                    _focusVisible={{ borderColor: 'chat.focus', boxShadow: '0 0 0 1px var(--chakra-colors-chat-focus)' }}
                  />
                  <Icon
                    as={IconPencil}
                    aria-hidden
                    boxSize='14px'
                    color={muted}
                    position='absolute'
                    top='11px'
                    right='34px'
                    pointerEvents='none'
                  />
                  <IconButton
                    aria-label={translate('dropQuery')}
                    title={translate('dropQuery')}
                    icon={<IconX size={14} />}
                    size='xs'
                    variant='ghost'
                    position='absolute'
                    top='6px'
                    right='6px'
                    onClick={() => setDropped(prev => [...prev, i])}
                  />
                </Box>
                <FormErrorMessage>{translate('queryTooLong')}</FormErrorMessage>
                {findings.length > 0 && (
                  <Flex id={findingsId} gap={3} mt={1} flexWrap='wrap'>
                    {findings.map((finding, k) => {
                      // A listed name whose words stand apart was never marked, so never struck.
                      const removed = contains(search.query, finding) && !contains(edits[i], finding)
                      const category = translate(`categories.${finding.category}`)
                      return (
                        <Text
                          key={k}
                          as={removed ? 's' : undefined}
                          fontSize='xs'
                          color={removed ? removedColor : muted}
                        >
                          {`${category}: ${finding.text}`}
                        </Text>
                      )
                    })}
                  </Flex>
                )}
              </FormControl>
            )}
          </Box>
        )
      })}

      <Flex mt={4} gap={2} justify='flex-end' flexWrap='wrap'>
        <Button
          type='button'
          variant='ghost'
          borderRadius='full'
          size='sm'
          leftIcon={<IconShieldCheck size={16} />}
          onClick={() => onResolve?.('internal')}
          isDisabled={!onResolve}
        >
          {translate(approval.has_internal_tools ? 'useInternal' : 'continueWithoutInternet')}
        </Button>
        <Button
          type='submit'
          colorScheme='primaryScheme'
          borderRadius='full'
          size='sm'
          px={5}
          leftIcon={<IconSend size={16} />}
          isDisabled={!canSend}
        >
          {translate('sendSearch')}
        </Button>
      </Flex>
    </Box>
  )
}
